// "Heute Soll / Ist / Rest / Überfällig" — Modül üstünde banner olarak.
// Dashboard için: toplam; Übersicht için: modül bazlı.

import { useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../lib/api';

function useAutoRefresh(reload: () => void, ms = 30_000) {
  useEffect(() => {
    const t = setInterval(reload, ms);
    const onVis = () => { if (document.visibilityState === 'visible') reload(); };
    const onFocus = () => reload();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function Bar({ soll, ist }: { soll: number; ist: number }) {
  const pct = soll === 0 ? (ist > 0 ? 100 : 0) : Math.min(100, Math.round((ist / soll) * 100));
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full transition-all ${
          pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-sky-500' : pct >= 30 ? 'bg-amber-500' : 'bg-red-500'
        }`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-600 tabular-nums w-10 text-right">{pct}%</span>
    </div>
  );
}

export function HeuteModulWidget({ modul, label }: { modul: string; label: string }) {
  const h = useApi(() => api.sessions.heuteModule(modul), [modul]);
  useAutoRefresh(() => h.reload());
  const d = h.data?.data;

  if (!d) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-900">
          Heute · {label}
        </h2>
        <div className="text-xs text-slate-500">
          {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-3">
        <Stat label="Soll (fällig heute)"   value={d.soll}  tone="sky" />
        <Stat label="Ist (fertig heute)"    value={d.ist}   tone="emerald" />
        <Stat label="Rest (offen heute)"    value={d.rest}  tone="amber" />
        <Stat label="Überfällig"             value={d.ueberfaellig} tone={d.ueberfaellig > 0 ? 'red' : 'slate'} />
      </div>
      <Bar soll={d.soll} ist={d.ist} />
      {d.offene_stunden !== null && (
        <div className="mt-2 text-xs text-slate-500">
          Offene Stunden bis heute: <strong className="text-slate-700">{d.offene_stunden} h</strong>
        </div>
      )}
    </div>
  );
}

export function HeuteGesamtWidget() {
  const h = useApi(() => api.sessions.heute(), []);
  useAutoRefresh(() => h.reload());
  if (!h.data) return null;
  const t = h.data.total;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-900">Heute Gesamt (alle Module)</h2>
        <div className="text-xs text-slate-500">
          {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Stat label="Soll"        value={t.soll}        stunden={t.soll_stunden}        tone="sky" />
        <Stat label="Ist"         value={t.ist}         stunden={t.ist_stunden}         tone="emerald" />
        <Stat label="Rest"        value={t.rest}        stunden={t.rest_stunden}        tone="amber" />
        <Stat label="Überfällig"  value={t.ueberfaellig} stunden={t.ueberfaellig_stunden} tone={t.ueberfaellig > 0 ? 'red' : 'slate'} />
      </div>
      <Bar soll={t.soll_stunden} ist={t.ist_stunden} />
      <div className="text-[11px] text-slate-400 mt-1 mb-2">
        Bar = Auslastung in Stunden (Ist ÷ Soll)
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        {h.data.data.map(m => (
          <div key={m.modul} className="bg-slate-50 rounded-lg p-2 border border-slate-200">
            <div className="text-xs text-slate-600 font-medium">{m.label}</div>
            <div className="text-[11px] mt-1 flex flex-wrap gap-x-2 tabular-nums">
              <span className="text-sky-700">Soll {m.soll} ({m.soll_stunden}h)</span>
              <span className="text-emerald-700">Ist {m.ist} ({m.ist_stunden}h)</span>
              <span className="text-amber-700">Rest {m.rest} ({m.rest_stunden}h)</span>
            </div>
            <Bar soll={m.soll_stunden} ist={m.ist_stunden} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, stunden, tone }: {
  label: string; value: number; stunden?: number;
  tone: 'sky' | 'emerald' | 'amber' | 'red' | 'slate';
}) {
  const colors: Record<string, string> = {
    sky:     'bg-sky-50 border-sky-200 text-sky-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    red:     'bg-red-50 border-red-200 text-red-800',
    slate:   'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-lg p-3 border ${colors[tone]}`}>
      <div className="text-[10px] uppercase font-semibold opacity-75">{label}</div>
      <div className="text-2xl font-bold mt-0.5 tabular-nums">
        {value}
        {stunden !== undefined && (
          <span className="ml-2 text-sm font-medium opacity-75">· {stunden}h</span>
        )}
      </div>
    </div>
  );
}
